import './MenuDrawer.less';

import { useLocalStorageState } from 'ahooks';
import classNames from 'classnames';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import IconChevron from '@/assets/svg/icon_chevron_d_dark.svg?react';
import IconGithub from '@/assets/svg/icon_more_github.svg?react';
import IconLang from '@/assets/svg/icon_more_lang.svg?react';
import Drawer from '@/components/Drawer';
import { useLangConfigs } from '@/components/LanguageSelector';
import SocialList from '@/components/List/SocialList';
import { LANGUAGE_KEY } from '@/constants';
import { FAQ_LINKS } from '@/constants/links';
import ClearCacheBtn from '@/features/user/UserAddressModal/ClearCacheBtn';
import { useChainShortName } from '@/hooks/web3/useChain';
import SwitchTheme from '@/layout/Header/SwitchTheme';
import IconDocs from '@/layout/Header/headerMenuDropdown/assets/icon_more_docs.svg?react';
import IconFaq from '@/layout/Header/headerMenuDropdown/assets/icon_more_faq.svg?react';
import IconWhitepaper from '@/layout/Header/headerMenuDropdown/assets/icon_more_whitepaper.svg?react';
import IconTutorial from '@/pages/mobile/assets/svg/icon_more_tutorial.svg?react';
import _ from 'lodash';

import IconCheckLinear from './assets/solar_check-circle-linear.svg?react';

interface IPropTypes {
  open: boolean;
  onClose: () => void;
}

const MenuDrawer: FC<IPropTypes> = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const chainShortName = useChainShortName();
  const [lastFutures] = useLocalStorageState('last_futures', { defaultValue: 'trade', listenStorageChange: true });

  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);

  const toggleMore = () => setIsMoreOpen(!isMoreOpen);

  const isActive = (path: string) => location.pathname.includes(path);

  const langConfigs = useLangConfigs();

  const selectLang = useCallback(
    async (lang: string) => {
      await i18n.changeLanguage(lang);
      localStorage.setItem(LANGUAGE_KEY, lang);
      setIsLangOpen(false);
    },
    [i18n],
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width="80%"
      className="syn-mobile-menu-drawer"
      title={
        isLangOpen ? (
          <div className="lang-header">
            <div className="lang-back" onClick={() => setIsLangOpen(false)}>
              <IconChevron className="back-icon" />
              <span>{t('common.dropdownMenu.language')}</span>
            </div>
          </div>
        ) : null
      }
      footer={null}
      closable={!isLangOpen}>
      {isLangOpen ? (
        <div className="syn-mobile-menu-drawer-body lang-mode">
          <div className="lang-list">
            {_.values(langConfigs).map((langConfig) => (
              <div
                key={langConfig.enShortName}
                className={classNames('lang-item', {
                  selected:
                    i18n.language === langConfig.enShortName || i18n.language?.startsWith(langConfig.enShortName),
                })}
                onClick={() => selectLang(langConfig.enShortName)}>
                <div className="lang-info">
                  <img src={langConfig.src} alt={langConfig.name} className="lang-icon" />
                  <span className="lang-name">{langConfig.name}</span>
                </div>
                {(i18n.language === langConfig.enShortName || i18n.language?.startsWith(langConfig.enShortName)) && (
                  <IconCheckLinear className="check-icon" />
                )}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="syn-mobile-menu-drawer-body">
          <ul className="syn-mobile-menu-drawer-list">
            <li className="syn-mobile-menu-drawer-item">
              <Link
                to={`${lastFutures === 'earn' ? '/earn' : `/trade`}/${chainShortName}`}
                onClick={onClose}
                className={classNames({ active: isActive('/trade') || isActive('/earn') })}>
                {t('header.menu.Futures')}
              </Link>
            </li>
            <li className="syn-mobile-menu-drawer-item">
              <Link to="/portfolio" onClick={onClose} className={classNames({ active: isActive('/portfolio') })}>
                {t('header.menu.Portfolio')}
              </Link>
            </li>

            <li className={classNames('syn-mobile-menu-drawer-item more-section', { open: isMoreOpen })}>
              <button className="menu-item-content" onClick={toggleMore}>
                <span>{t('common.more')}</span>
                <IconChevron className={classNames('chevron-icon', { rotate: isMoreOpen })} />
              </button>
              <ul className={classNames('syn-mobile-menu-drawer-sub-menu', { open: isMoreOpen })}>
                <li className="syn-mobile-menu-drawer-item">
                  <a href={FAQ_LINKS.FAQ} target="_blank" rel="noreferrer" onClick={onClose}>
                    <span className="menu-item-label">
                      <IconFaq /> {t('common.dropdownMenu.faq')}
                    </span>
                    <IconChevron className="sub-menu-chevron" />
                  </a>
                </li>
                <li className="syn-mobile-menu-drawer-item">
                  <a href="https://docs.synfutures.com" target="_blank" rel="noreferrer" onClick={onClose}>
                    <span className="menu-item-label">
                      <IconDocs /> {t('common.dropdownMenu.docs')}
                    </span>
                    <IconChevron className="sub-menu-chevron" />
                  </a>
                </li>
                <li className="syn-mobile-menu-drawer-item">
                  <a
                    href="https://www.synfutures.com/v3-whitepaper.pdf"
                    target="_blank"
                    rel="noreferrer"
                    onClick={onClose}>
                    <span className="menu-item-label">
                      <IconWhitepaper /> {t('common.dropdownMenu.whitepaper')}
                    </span>
                    <IconChevron className="sub-menu-chevron" />
                  </a>
                </li>
                <li className="syn-mobile-menu-drawer-item">
                  <a href="https://github.com/SynFutures/" target="_blank" rel="noreferrer" onClick={onClose}>
                    <span className="menu-item-label">
                      <IconGithub /> {t('common.dropdownMenu.github')}
                    </span>
                    <IconChevron className="sub-menu-chevron" />
                  </a>
                </li>
                <li className="syn-mobile-menu-drawer-item">
                  <a href="https://www.youtube.com/@SynFutures" target="_blank" rel="noreferrer" onClick={onClose}>
                    <span className="menu-item-label">
                      <IconTutorial /> {t('common.tutorials')}
                    </span>
                    <IconChevron className="sub-menu-chevron" />
                  </a>
                </li>
                <li className="syn-mobile-menu-drawer-item">
                  <div className="menu-item-content-lang" onClick={() => setIsLangOpen(true)}>
                    <span className="menu-item-label">
                      <IconLang /> {t('common.dropdownMenu.language')}
                    </span>
                    <div className="menu-item-right">
                      <span className="lang-text">{i18n.language?.toUpperCase()}</span>
                      <IconChevron className="sub-menu-chevron" />
                    </div>
                  </div>
                </li>
              </ul>
            </li>
          </ul>
          <div className="syn-mobile-menu-drawer-footer">
            <div className="footer-social">
              <SocialList />
            </div>
            <div className="footer-settings">
              <ClearCacheBtn />
              <SwitchTheme />
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default MenuDrawer;
